/*
 * The local draft layer's store: the memory mirror the composer reads synchronously, and the
 * IndexedDB rows behind it that make a draft outlive a reload, a crash and a server restart
 * (spec 08 §4, A19). Two rules shape it: nothing is ever truncated — a draft over the cap is
 * refused with a sentence, never shortened — and a browser that refuses storage gets a working
 * memory-only store plus a flag the interface turns into a banner, because silence there is the
 * one outcome worse than losing the draft.
 */

import { DRAFT_SCHEMA, isQuotaError, openDraftsDb, type DraftsDb, type StoredDraft } from "./db.ts"
import type { AttachmentRef, DraftFields, DraftRecord, TargetKey } from "./types.ts"

/**
 * The local cap. Well above the server's 64 KiB mirror cap and the 12 000-character message limit,
 * because the local copy is the one that must never lose a paragraph; the server refuses its own.
 */
export const LOCAL_DRAFT_MAX_BYTES = 256 * 1024

/** Drafts nobody touched for this long are pruned at the next open (A19). */
export const LOCAL_DRAFT_TTL_MS = 180 * 24 * 60 * 60 * 1000

/** Two consecutive failed writes raise the banner (spec 08 §6.8); one is a blip. */
const FAILURES_BEFORE_BANNER = 2

export type StorageMode = "opening" | "idb" | "memory"

export type PutResult = "stored" | "tooLarge"

const encoder = new TextEncoder()

export interface DraftStoreOptions {
	open?: () => Promise<DraftsDb | undefined>
	now?: () => number
}

export class DraftStore {
	private readonly rows = new Map<TargetKey, StoredDraft>()
	private db: DraftsDb | undefined
	private storageMode: StorageMode = "opening"
	private failures = 0
	private bannerRaised = false
	private readonly listeners = new Set<() => void>()
	private readonly now: () => number
	/** Resolves once the disk has been read; `get` before that only sees the memory mirror. */
	readonly ready: Promise<void>

	constructor(opts: DraftStoreOptions = {}) {
		this.now = opts.now ?? (() => Date.now())
		this.ready = this.open(opts.open ?? openDraftsDb)
	}

	private async open(open: () => Promise<DraftsDb | undefined>): Promise<void> {
		this.db = await open()
		if (this.db === undefined) {
			this.storageMode = "memory"
			this.bannerRaised = true
			this.notify()
			return
		}
		try {
			for (const row of await this.db.loadDrafts()) {
				// A row typed before the composer asked the disk wins over the disk.
				if (!this.rows.has(row.key)) this.rows.set(row.key, row)
			}
			this.storageMode = "idb"
		} catch {
			this.storageMode = "memory"
			this.bannerRaised = true
		}
		this.notify()
		void this.prune()
	}

	/** The database this store writes through, for the outbox's send transaction. */
	database(): DraftsDb | undefined {
		return this.db
	}

	mode(): StorageMode {
		return this.storageMode
	}

	/** True while the interface should say that drafts do not survive on this device. */
	banner(): boolean {
		return this.bannerRaised
	}

	subscribe(listener: () => void): () => void {
		this.listeners.add(listener)
		return () => {
			this.listeners.delete(listener)
		}
	}

	private notify(): void {
		for (const l of this.listeners) l()
	}

	/** The draft under a key as the memory mirror holds it; a row with an unknown stamp too. */
	get(key: TargetKey): DraftRecord | undefined {
		const row = this.rows.get(key)
		return row === undefined ? undefined : toRecord(row)
	}

	/** Every draft this device holds, newest first — what the server mirror offers the server. */
	all(): DraftRecord[] {
		return [...this.rows.values()].sort((a, b) => b.updatedAt - a.updatedAt).map(toRecord)
	}

	/**
	 * Writes a draft: the memory mirror at once, the disk when it gets there. An empty draft — no
	 * text, no file and no settings — is a deletion, and one over the cap is refused whole: the
	 * caller keeps what it has and says why. `fields` left out keeps no settings, which is what every
	 * surface that has none wants.
	 */
	async put(
		key: TargetKey,
		markdown: string,
		attachments: AttachmentRef[],
		fields?: DraftFields,
	): Promise<PutResult> {
		const settings = fields !== undefined && Object.keys(fields).length > 0 ? fields : undefined
		if (markdown === "" && attachments.length === 0 && settings === undefined) {
			await this.remove(key)
			return "stored"
		}
		if (encoder.encode(markdown).length > LOCAL_DRAFT_MAX_BYTES) return "tooLarge"
		const row: StoredDraft = {
			key,
			markdown,
			attachments,
			...(settings === undefined ? {} : { fields: settings }),
			updatedAt: this.now(),
			rev: this.rows.get(key)?.rev ?? 0,
			schema: DRAFT_SCHEMA,
		}
		this.rows.set(key, row)
		this.notify()
		await this.persist(row)
		return "stored"
	}

	/**
	 * Adds a block to the draft under `key`, after whatever is already in it — how a surface writes
	 * into a conversation nobody has open (a forward, X-A4 N11). The existing text is kept and the
	 * block is separated from it by a blank line, because the draft layer's whole promise is that
	 * nothing typed is ever lost, least of all to a feature that writes on somebody's behalf.
	 *
	 * It waits for the disk first. `put` before the rows are read wins over them by design — a
	 * keystroke that landed before the composer asked is newer than the disk — so appending without
	 * this `await` would silently drop the paragraph left in that box yesterday.
	 */
	async append(key: TargetKey, markdown: string): Promise<PutResult> {
		await this.ready
		const held = this.rows.get(key)
		const before = held?.markdown ?? ""
		const text = before === "" ? markdown : `${before.replace(/\n+$/, "")}\n\n${markdown}`
		return this.put(key, text, held?.attachments ?? [], held?.fields)
	}

	/**
	 * Takes a draft that was written elsewhere — another tab, the outbox handing text back — into
	 * the mirror without touching the disk again.
	 */
	adopt(record: DraftRecord): void {
		this.rows.set(record.key, {
			...record,
			rev: record.rev ?? 0,
			schema: DRAFT_SCHEMA,
		})
		this.notify()
	}

	/**
	 * Records which revision of the server mirror a draft's text stands at (`mirror.ts`). It is a fact
	 * about the agreement with the server and not about the text, so it goes through to the disk — a
	 * revision lost in a reload would make the next write look like a stale one and be refused — and it
	 * notifies nobody: no surface renders a revision, and waking every reader for it would send the
	 * mirror straight back around its own loop.
	 */
	async stamp(key: TargetKey, rev: number): Promise<void> {
		const row = this.rows.get(key)
		if (row === undefined || row.rev === rev) return
		const next: StoredDraft = { ...row, rev }
		this.rows.set(key, next)
		await this.persist(next)
	}

	async remove(key: TargetKey): Promise<void> {
		if (!this.rows.delete(key)) return
		this.notify()
		if (this.db === undefined) return
		try {
			await this.db.deleteDraft(key)
		} catch {
			// Nothing left to lose: the mirror is already empty and the row dies at the next put.
		}
	}

	/** Forgets the mirror's copy after the outbox's send transaction deleted the row. */
	forget(key: TargetKey): void {
		if (this.rows.delete(key)) this.notify()
	}

	/**
	 * Drops every draft, on the disk and in the mirror. The one place work is lost deliberately:
	 * a different account has signed in on this device and none of this is theirs
	 * (core/session/device.ts). It waits for the open, so a store still reading the disk cannot
	 * put the rows back afterwards.
	 */
	async discardAll(): Promise<void> {
		await this.ready
		this.rows.clear()
		this.notify()
		try {
			await this.db?.clear()
		} catch {
			// A refused write leaves rows the next claim meets again; the mirror is already empty.
		}
	}

	/** Removes drafts untouched for the TTL; returns how many went. */
	async prune(): Promise<number> {
		const cutoff = this.now() - LOCAL_DRAFT_TTL_MS
		const stale = [...this.rows.values()].filter((r) => r.updatedAt < cutoff)
		for (const r of stale) await this.remove(r.key)
		return stale.length
	}

	/**
	 * One disk write with the quota rule of spec 08 §6.8: on a full quota the oldest draft that is
	 * not the one being typed is evicted and the write retried once; failures are counted, and the
	 * banner goes up after two in a row.
	 */
	private async persist(row: StoredDraft): Promise<void> {
		if (this.db === undefined) return
		try {
			await this.db.putDraft(row)
			this.failures = 0
		} catch (err) {
			if (isQuotaError(err) && (await this.evictOldest(row.key))) {
				try {
					await this.db.putDraft(row)
					this.failures = 0
					return
				} catch {
					// Counted below like the first failure.
				}
			}
			this.failures++
			if (this.failures >= FAILURES_BEFORE_BANNER && !this.bannerRaised) {
				this.bannerRaised = true
				this.notify()
			}
		}
	}

	private async evictOldest(except: TargetKey): Promise<boolean> {
		let oldest: StoredDraft | undefined
		for (const r of this.rows.values()) {
			if (r.key !== except && (oldest === undefined || r.updatedAt < oldest.updatedAt)) oldest = r
		}
		if (oldest === undefined) return false
		await this.remove(oldest.key)
		return true
	}
}

function toRecord(row: StoredDraft): DraftRecord {
	const record: DraftRecord = {
		key: row.key,
		markdown: typeof row.markdown === "string" ? row.markdown : "",
		attachments: Array.isArray(row.attachments) ? row.attachments : [],
		updatedAt: row.updatedAt,
	}
	// A row written before settings existed has none; a malformed one is read as none.
	if (typeof row.fields === "object" && row.fields !== null) record.fields = row.fields
	if (row.rev > 0) record.rev = row.rev
	return record
}
